import { useTranslation } from 'react-i18next';
import { Badge } from '@xxtract/ui';
import { formatInvoiceDate, isDueDatePassed } from '../format';
import type { Invoice } from '../invoices.api';

interface InvoiceStatusBadgeProps {
  status: Invoice['status'];
  overdue: Invoice['overdue'];
  dueDate: Invoice['dueDate'];
  locale: string;
}

/**
 * Status is always icon + label + color (NFR9) — never color alone. "Openstaand" (open) is a
 * neutral status, never rendered as an error/destructive color, per DESIGN.md.
 */
export function InvoiceStatusBadge({ status, overdue, dueDate, locale }: InvoiceStatusBadgeProps) {
  const { t } = useTranslation();

  if (status === 'paid') {
    return (
      <Badge data-testid="invoice-status-badge" variant="success" role="status">
        <span className="material-symbols-outlined text-[14px]" aria-hidden="true">check</span>
        {t('invoices.status.paid')}
      </Badge>
    );
  }

  if (status === 'open') {
    const label = overdue && dueDate
      ? t('invoices.status.overdueUntil', { date: formatInvoiceDate(dueDate, locale) })
      : t('invoices.status.open');
    return (
      <Badge data-testid="invoice-status-badge" variant="secondary" role="status">
        <span className="material-symbols-outlined text-[14px]" aria-hidden="true">schedule</span>
        {label}
      </Badge>
    );
  }

  // status === 'unknown': no ar_items match yet (h12-0 contract §1.3). NEVER claim "paid" —
  // but a passed due date still gets the neutral "open" treatment instead of a bare
  // "unknown" badge, so the customer isn't left guessing about an overdue invoice.
  if (isDueDatePassed(dueDate)) {
    return (
      <Badge data-testid="invoice-status-badge" variant="secondary" role="status">
        <span className="material-symbols-outlined text-[14px]" aria-hidden="true">schedule</span>
        {t('invoices.status.openDueDatePassed')}
      </Badge>
    );
  }

  return (
    <Badge data-testid="invoice-status-badge" variant="outline" role="status">
      <span className="material-symbols-outlined text-[14px]" aria-hidden="true">help</span>
      {t('invoices.status.unknown')}
    </Badge>
  );
}
